<template>
  <li class="rail-bet-row">
    <div class="rail-bet-row__head">
      <span class="rail-bet-row__table">{{ row.table }} #{{ row.round }}</span>

      <!-- Multi-selection ticket: "N BETS", which opens its selections below. -->
      <button
        v-if="row.betsCount"
        type="button"
        class="rail-bet-row__bets"
        :class="{ open: expanded }"
        @click="expanded = !expanded"
      >
        {{ row.betsCount }} {{ $t('Bets') }}
        <span class="rail-bet-row__bets-chevron">▾</span>
      </button>
      <!-- Triple, total, single, double: an icon badge; the rest a text pill. -->
      <TxMarketBadge v-else-if="row.market" class="rail-bet-row__market" :market="row.market" />
      <span v-else :class="['rail-bet-row__side', `rail-bet-row__side--${row.sideKey}`]">{{ row.side }}</span>
      <span class="rail-bet-row__stake">{{ row.stake }}</span>
    </div>

    <ul v-if="row.selections && expanded" class="rail-bet-row__selections">
      <li v-for="selection in row.selections" :key="selection.id" class="rail-bet-row__selection">
        <TxMarketBadge v-if="selection.market" :market="selection.market" />
        <span v-else :class="['rail-bet-row__side', `rail-bet-row__side--${selection.sideKey}`]">
          {{ selection.side }}
        </span>
        <span class="rail-bet-row__selection-stake">{{ selection.stake }}</span>
        <span :class="['rail-bet-row__payout', `rail-bet-row__payout--${selection.payoutKind || 'odds'}`]">
          {{ selection.payout }}
        </span>
      </li>
    </ul>

    <div class="rail-bet-row__foot">
      <!-- A settled Tài Xỉu bet shows its dice instead of a status pill. -->
      <span v-if="row.result" class="rail-bet-row__result">
        <img
          v-for="(die, index) in row.result.dice"
          :key="index"
          :src="diceImage(die)"
          :alt="`Die ${index + 1}: ${die}`"
        />
        <em class="rail-bet-row__total">= {{ row.result.total }}</em>
        <em class="rail-bet-row__outcome">{{ row.result.label }}</em>
      </span>
      <span v-else :class="['rail-bet-row__status', `rail-bet-row__status--${row.statusKey}`]">
        {{ $t(row.status) }}
      </span>

      <span :class="['rail-bet-row__payout', payoutClass]">{{ row.payout }}</span>
    </div>
  </li>
</template>

<script>
import TxMarketBadge from '@/components/tai-xiu/tx-market-badge.vue';

/*
 * One row of the CURRENT list in the desktop rail.
 *
 * Presentational only -- it takes a ready-made view model so the list can be
 * fed mock data now and the real feed later without touching this file.
 * Every colour and size here is measured from the Figma rail (node 457:6903),
 * not eyeballed.
 */
export default {
  name: 'RailBetRow',
  components: { TxMarketBadge },
  props: {
    // { table, round, screen, side, sideKey, market?, stake, status, statusKey,
    //   payout, payoutKind, result?: { dice: number[], total: number, label: string } }
    // `market` (utils/tai-xiu-market.ts) replaces the text pill when set.
    // A multi-selection ticket adds `betsCount` and `selections`:
    //   [{ id, side, sideKey, market?, stake, payout, payoutKind }].
    row: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      // A multi-selection ticket's selections are showing.
      expanded: false,
    };
  },
  computed: {
    payoutClass() {
      return `rail-bet-row__payout--${this.row.payoutKind || 'odds'}`;
    },
  },
  methods: {
    diceImage(face) {
      return require(`@/assets/svg/dice-items/dice-${face}.svg`);
    },
  },
};
</script>

<style lang="scss" scoped>
.rail-bet-row {
  // v2.1: 10/14 padding, the head and foot 7px apart.
  padding: 10px 14px;
  list-style: none;
  border-bottom: 1px solid #19253c;
}

.rail-bet-row__head,
.rail-bet-row__foot {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rail-bet-row__foot {
  margin-top: 7px;
}

.rail-bet-row__table {
  color: #a1bbc9;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.rail-bet-row__side {
  margin-left: auto;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  box-sizing: border-box;
  // Flex, not grid: a face market puts two or three dice in here side by side.
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  box-sizing: border-box;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  // The inherited line-height left the label low in the pill; 1 centres it.
  line-height: 1;
  text-align: center;
  white-space: nowrap;

  &--banker {
    background: #eb3323;
  }

  &--player {
    background: #0273e8;
  }

  // Tài Xỉu text markets wear the bet board's own zone colours (bet-board.vue).
  &--tai {
    background: linear-gradient(160deg, #c33a2f, #a01f1f 55%, #6b1414);
  }

  &--xiu {
    background: linear-gradient(200deg, #3a86dc, #1f62b8 50%, #0d3f7a);
  }

  &--chan,
  &--le {
    background: linear-gradient(180deg, #24a35c, #157140);
  }

  // Totals and face markets are the board's blue cells.
  &--sum,
  &--dice {
    background: #0273e8;
  }

  &--draw {
    background: #0eb14d;
  }
}

// The icon badge sits where the text pill would.
.rail-bet-row__market {
  margin-left: auto;
}

// Multi-selection ticket: "N BETS" pill (Multi-Selection Ticket Prototype v1.0).
.rail-bet-row__bets {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px;
  border: 1.5px solid #d9a72b;
  border-radius: 999px;
  background: transparent;
  color: #f3c54d;
  font-family: inherit;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;

  &.open {
    background: rgba(217, 167, 43, 0.18);
  }
}

.rail-bet-row__bets-chevron {
  display: inline-block;
  transition: transform 0.2s;

  .open & {
    transform: rotate(180deg);
  }
}

// Its selections: badge, stake and odds (or W/L), one line each.
.rail-bet-row__selections {
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
  border-radius: 6px;
  background: #0a1730;
}

.rail-bet-row__selection {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-bottom: 1px dashed #1f3a63;

  &:last-child {
    border-bottom: 0;
  }

  // The pill sits on the left here, not pushed right as in the row head.
  .rail-bet-row__side {
    margin-left: 0;
  }

  .rail-bet-row__payout--win,
  .rail-bet-row__payout--loss {
    font-size: 13px;
  }
}

.rail-bet-row__selection-stake {
  color: #dfe6f1;
  font-size: 13px;
  font-weight: 500;
}

.rail-bet-row__stake {
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  text-align: right;
}

/*
 * v2.1: a fixed 140x28 pill -- growing to fill the row stretched it all the way
 * to the payout.
 */
.rail-bet-row__status {
  flex: 0 1 140px;
  min-width: 0;
  height: 28px;
  box-sizing: border-box;
  border-radius: 6px;
  display: grid;
  place-items: center;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  border: 1px solid transparent;

  &--open {
    background: rgba(106, 145, 167, 0.3);
    color: #b2d6ea;
  }

  &--closing {
    border-color: rgba(239, 200, 127, 0.35);
    background: rgba(239, 200, 127, 0.09);
    color: #efc87f;
  }

  // Started and Ended are past betting, like Closed; Result waits for the settle.
  &--closed,
  &--started,
  &--ended,
  &--result {
    border-color: rgba(178, 214, 234, 0.25);
    background: rgba(178, 214, 234, 0.06);
    color: #a1bbc9;
  }

  // The round is under way: gold, as Closing Soon, so it reads as live.
  &--started {
    border-color: rgba(239, 200, 127, 0.35);
    color: #efc87f;
  }

  /*
   * Settled rows report the round, so they take the winning side's colour --
   * not the member's result, which the payout beside them already shows.
   */
  &--banker {
    border-color: rgba(235, 51, 35, 0.45);
    background: rgba(235, 51, 35, 0.1);
    color: #ff6a5c;
  }

  &--player {
    border-color: rgba(2, 115, 232, 0.5);
    background: rgba(2, 115, 232, 0.12);
    color: #5faef2;
  }

  &--draw {
    border-color: rgba(14, 177, 77, 0.45);
    background: rgba(14, 177, 77, 0.12);
    color: #44f589;
  }

  &--void,
  &--settled {
    border-color: rgba(178, 214, 234, 0.25);
    background: rgba(178, 214, 234, 0.06);
    color: #a1bbc9;
  }
}

/* Settled Tài Xỉu: 162x34 box (fixed, as the pill), #3996E5 at 10% with a solid border. */
.rail-bet-row__result {
  flex: 0 1 140px;
  min-width: 0;
  height: 28px;
  padding: 0 8px;
  border: 1px solid #3996e5;
  border-radius: 6px;
  background: rgba(57, 150, 229, 0.1);
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 4px;

  img {
    width: 14px;
    height: 14px;
    display: block;
  }
}

.rail-bet-row__total {
  margin-left: 2px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  font-style: normal;
}

.rail-bet-row__outcome {
  margin-left: auto;
  color: #5faef2;
  font-size: 12px;
  font-weight: 600;
  font-style: normal;
}

.rail-bet-row__payout {
  // Pushed to the row's right edge now that the pill no longer fills the gap.
  margin-left: auto;
  min-width: 52px;
  font-weight: 600;
  text-align: right;
  white-space: nowrap;

  // Pending bet: the odds it was taken at.
  &--odds {
    color: #6a91a7;
    font-size: 12px;
    font-weight: 500;
  }

  &--win {
    color: #44f589;
    font-size: 15px;
  }

  &--loss {
    color: #ff5e50;
    font-size: 15px;
  }
}
</style>
